import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../firebase';
import { fetchUserInfo } from '../services/userService';
import { makeAuthenticatedRequest } from '../services/api';
import { FaUserCircle, FaLock, FaSave, FaArrowLeft, FaEye, FaEyeSlash, FaBell } from 'react-icons/fa';
import { useUserPreferences } from '../context/UserPreferencesContext';
import ToggleSwitch from '../components/ToggleSwitch';

const AccountPage = () => {
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  
  // Profile form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profileMessage, setProfileMessage] = useState(null);
  const [profileError, setProfileError] = useState(null);

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [showPasswords, setShowPasswords] = useState(false);

  // Notification preferences (ticket 101). The provider above this page has already loaded them.
  const { emailNotificationsEnabled, setEmailNotifications, ready: prefsReady, loadFailed: prefsLoadFailed } = useUserPreferences();
  const [emailStatus, setEmailStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const emailToggleId = useRef(0);

  const navigate = useNavigate();

  useEffect(() => {
    const loadUser = async () => {
      try {
        const data = await fetchUserInfo();
        if (data) {
          setUserInfo(data);
          setFirstName(data.firstName || '');
          setLastName(data.lastName || '');
        }
      } catch (err) {
        console.error('Failed to fetch user info', err);
        setProfileError('Could not load user profile.');
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, []);

  // Clears the "Saved" note on its own; a failure stays put until the next attempt.
  useEffect(() => {
    if (emailStatus !== 'saved') return undefined;
    const timer = setTimeout(() => setEmailStatus(null), 3000);
    return () => clearTimeout(timer);
  }, [emailStatus]);

  const handleToggleEmails = async (enabled) => {
    const id = ++emailToggleId.current;
    setEmailStatus('saving');
    const saved = await setEmailNotifications(enabled);
    if (id !== emailToggleId.current) return; // a newer toggle owns the status line
    setEmailStatus(saved ? 'saved' : 'error');
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileMessage(null);
    setProfileError(null);

    try {
      await makeAuthenticatedRequest('/data/update-item', {
        method: 'POST',
        body: {
          collection: 'users',
          id: userInfo.id || userInfo.uid,
          data: {
            firstName,
            lastName,
          }
        }
      });
      setProfileMessage('Profile updated successfully!');
      setUserInfo({ ...userInfo, firstName, lastName });
    } catch (err) {
      console.error('Error updating profile:', err);
      setProfileError('Failed to update profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setSavingPassword(true);
    setPasswordMessage(null);
    setPasswordError(null);

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      setSavingPassword(false);
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long.');
      setSavingPassword(false);
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No authenticated user found.');
      
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      await updatePassword(user, newPassword);
      setPasswordMessage('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Error updating password:', err);
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setPasswordError('Incorrect current password.');
      } else {
        setPasswordError(err.message || 'Failed to update password.');
      }
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center text-gray-500">
        Loading account details...
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-6 flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="mr-4 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <FaArrowLeft className="text-xl" />
        </button>
        <h1 className="text-3xl font-bold text-gray-800 flex items-center">
          <FaUserCircle className="mr-3 text-jims-cyan-dark" />
          My Account
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Profile Info Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4 border-b pb-2 text-gray-700">Profile Information</h2>
          
          {profileMessage && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded text-sm">{profileMessage}</div>}
          {profileError && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">{profileError}</div>}

          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                readOnly
                value={userInfo?.email || auth.currentUser?.email || ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Type</label>
              <input
                type="text"
                readOnly
                value={userInfo?.userType || 'N/A'}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed capitalize"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jims-cyan"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jims-cyan"
              />
            </div>

            <button
              type="submit"
              disabled={savingProfile}
              className="mt-4 w-full flex items-center justify-center px-4 py-2 bg-jims-cyan-dark text-white rounded-md hover:bg-jims-cyan transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jims-cyan-dark disabled:opacity-50"
            >
              <FaSave className="mr-2" />
              {savingProfile ? 'Saving...' : 'Update Profile'}
            </button>
          </form>
        </div>

        {/* Change Password Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4 border-b pb-2 text-gray-700 flex items-center">
            <FaLock className="mr-2" />
            Change Password
          </h2>

          {passwordMessage && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded text-sm">{passwordMessage}</div>}
          {passwordError && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">{passwordError}</div>}

          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jims-cyan"
                placeholder="Enter current password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 pt-6 flex items-center text-gray-500 hover:text-gray-700"
                onClick={() => setShowPasswords(!showPasswords)}
              >
                {showPasswords ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jims-cyan"
                placeholder="Enter new password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 pt-6 flex items-center text-gray-500 hover:text-gray-700"
                onClick={() => setShowPasswords(!showPasswords)}
              >
                {showPasswords ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jims-cyan"
                placeholder="Confirm new password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 pt-6 flex items-center text-gray-500 hover:text-gray-700"
                onClick={() => setShowPasswords(!showPasswords)}
              >
                {showPasswords ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            <button
              type="submit"
              disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword}
              className="mt-4 w-full flex items-center justify-center px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-800 disabled:opacity-50"
            >
              <FaLock className="mr-2" />
              {savingPassword ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>

        {/* Notification Preferences Section */}
        <div className="bg-white rounded-lg shadow-md p-6 md:col-span-2">
          <h2 className="text-xl font-bold mb-4 border-b pb-2 text-gray-700 flex items-center">
            <FaBell className="mr-2" aria-hidden="true" />
            Notifications
          </h2>

          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <label id="automated-emails-label" htmlFor="automated-emails" className="block text-sm font-medium text-gray-700 cursor-pointer">
                Automated emails
              </label>
              <p id="automated-emails-help" className="text-sm text-gray-500 mt-1">
                Notices sent to every admin, like document expiry reminders and duplicate franchisee alerts.
                Password resets and anything addressed to you personally are always sent.
              </p>
            </div>
            <ToggleSwitch
              id="automated-emails"
              checked={emailNotificationsEnabled}
              onChange={handleToggleEmails}
              disabled={!prefsReady || prefsLoadFailed}
              labelledBy="automated-emails-label"
              describedBy="automated-emails-help"
              className="mt-1"
            />
          </div>

          <p className="text-sm mt-3 min-h-[1.25rem]" aria-live="polite">
            {!prefsReady && <span className="text-gray-500">Loading your current setting…</span>}
            {prefsReady && prefsLoadFailed && (
              <span className="text-red-600">Could not load your current setting. Please refresh the page.</span>
            )}
            {prefsReady && !prefsLoadFailed && emailStatus === 'saving' && <span className="text-gray-500">Saving…</span>}
            {prefsReady && !prefsLoadFailed && emailStatus === 'saved' && <span className="text-green-700">Saved.</span>}
            {prefsReady && !prefsLoadFailed && emailStatus === 'error' && (
              <span className="text-red-600">Could not save that. Your setting has not changed, please try again.</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
